import React, { useState, useEffect, useMemo } from "react"; 
import { Card, Row, Col, DatePicker, Space, Table, Tag, Select, Typography, Button, Spin } from "antd";
import { PhoneOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined, FilePdfOutlined } from "@ant-design/icons";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";
import api from '../api/Api';


const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const GestaoChamadasAgentes = () => {
  const [datas, setDatas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dadosAgentes, setDadosAgentes] = useState([]);
  const [chamadas, setChamadas] = useState([]);
  const [agenteSelecionado, setAgenteSelecionado] = useState(null);
  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [loadingDados, setLoadingDados] = useState(false);


  

  // Buscar lista de agentes quando o período for selecionado
  const fetchDadosAgentes = async () => {
    if (!datas || !datas[0] || !datas[1]) return;
    
    try {
      setLoadingDados(true);
      const payload = {
        dataInicio: datas[0].format("YYYY-MM-DD"),
        dataFim: datas[1].format("YYYY-MM-DD"),
        kkk:'p%QI!(0_WQJbq~EjTOVu5H;L_S_=J6=J'
      };
      const response = await api.ChamadaAgente(payload);
      if (Array.isArray(response)) {
        setDadosAgentes(response);
      }
    } catch (error) {
      console.error("Erro ao buscar dados dos agentes:", error);
    } finally {
      setLoadingDados(false);
    }
  };

  // Buscar chamadas detalhadas quando o período for selecionado
  const fetchChamadasAgentes = async () => {
    if (!datas || !datas[0] || !datas[1]) return;
    
    try {
      setLoading(true);
      const payload = {
        dataInicio: datas[0].format("YYYY-MM-DD"),
        dataFim: datas[1].format("YYYY-MM-DD"),
      kkk:'p%QI!(0_WQJbq~EjTOVu5H;L_S_=J6=J'

      };
      const response = await api.ListRegistoAgente(payload);
      if (Array.isArray(response)) setChamadas(response);
    } catch (error) {
      console.error("Erro ao buscar chamadas dos agentes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Quando o período muda, buscar dados
  useEffect(() => {
    if (datas && datas[0] && datas[1]) {
      fetchDadosAgentes();
      fetchChamadasAgentes();
      // Limpar seleção quando mudar o período
      setAgenteSelecionado(null);
      setDadosGrafico([]);
    }
  }, [datas]);

  // Gerar dados para o gráfico quando um agente for selecionado
  useEffect(() => {
    if (agenteSelecionado && chamadas.length > 0) {
      const chamadasAgente = chamadas.filter(chamada => chamada.agente === agenteSelecionado);
      
      // Agrupar chamadas por dia
      const chamadasPorDia = {};
      chamadasAgente.forEach(chamada => {
        const data = dayjs(chamada.data).format('DD/MM/YYYY');
        if (!chamadasPorDia[data]) {
          chamadasPorDia[data] = 0;
        }
        chamadasPorDia[data]++;
      });

      // Converter para array para o gráfico
      const dadosGraficoArray = Object.keys(chamadasPorDia).map(data => ({
        data: data,
        chamadas: chamadasPorDia[data]
      })).sort((a, b) => dayjs(a.data, 'DD/MM/YYYY').valueOf() - dayjs(b.data, 'DD/MM/YYYY').valueOf());

      setDadosGrafico(dadosGraficoArray);
    } else {
      setDadosGrafico([]);
    }
  }, [agenteSelecionado, chamadas]);

  const agenteDetalhes = useMemo(() => {
    if (!agenteSelecionado) return null;
    return dadosAgentes.find(agente => agente.agente === agenteSelecionado);
  }, [agenteSelecionado, dadosAgentes]);

  const filteredChamadas = useMemo(() => {
    if (!agenteSelecionado) return [];
    return chamadas.filter(chamada => chamada.agente === agenteSelecionado);
  }, [agenteSelecionado, chamadas]);

  const columns = [
    {
      title: "Data e hora",
      dataIndex: "data",
      key: "data",
      width: 160,
      render: (text) => (
        <div>
          <div style={{ fontWeight: 500 }}>{dayjs(text).format("DD/MM/YYYY")}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {dayjs(text).format("HH:mm")}
          </Text>
        </div>
      ),
    },
     
    {
      title: "Fila",
      dataIndex: "fila",
      key: "fila",
      width: 120,
    },
    {
      title: "Tempo de espera",
      dataIndex: "espera",
      key: "espera",
      width: 80,
      align: "center",
      render: (duracao) => <Text>{duracao}</Text>,
    },
    {
      title: "Duração",
      dataIndex: "duracao",
      key: "duracao",
      width: 80,
      align: "center",
      render: (duracao) => <Text>{duracao}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => {
        const statusMap = {
          "completed": { text: "Completada", color: "#52c41a", icon: <CheckCircleOutlined /> },
          "noanswer": { text: "Sem resposta", color: "#ff4d4f", icon: <CloseCircleOutlined /> },
          "abandoned": { text: "Abandonada", color: "#fa8c16", icon: <ExclamationCircleOutlined /> },
        };
        
        const config = statusMap[status] || { text: status, color: "#d9d9d9", icon: <InfoCircleOutlined /> };
        
        return (
          <Tag 
            color={config.color} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              padding: '4px 8px',
              borderRadius: '6px',
              border: 'none',
              margin: 0
            }}
          >
            {config.icon}
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: "Operador",
      dataIndex: "agente",
      key: "agente",
      width: 120,
    },
  ];

  const stats = useMemo(() => {
    const lista = [
      { 
        title: "Total de chamadas atendidas", 
        value: agenteDetalhes?.total_atendidas || "0", 
        color: "#1890ff",
        icon: <PhoneOutlined />,
      },
      { 
        title: "Duração média", 
        value: agenteDetalhes?.duracao_media_completed || "00:00:00", 
        color: "#52c41a",
        icon: <CheckCircleOutlined />,
      },
      { 
        title: "Duração máxima", 
        value: agenteDetalhes?.duracao_maxima || "00:00:00", 
        color: "#faad14",
        icon: <ExclamationCircleOutlined />,
      },
    ];

 
    if (
      datas &&
      datas[0] &&
      datas[1] &&
      datas[0].isSame(datas[1], "day") &&
      (agenteDetalhes?.periodo_trabalho || agenteDetalhes?.horas_trabalhadas)
    ) {
      lista.push(
        {
          title: "Período de Trabalho",
          value: agenteDetalhes?.periodo_trabalho || "-",
          color: "#722ed1",
          icon: <InfoCircleOutlined />,
        },
        {
          title: "Horas Trabalhadas",
          value: agenteDetalhes?.horas_trabalhadas || "-",
          color: "#eb2f96",
          icon: <InfoCircleOutlined />,
        }
      );
    }

    return lista;
  }, [agenteDetalhes, datas]);


  return (
 <div
  style={{
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "linear-gradient(135deg, #f5f7fa 0%, #e4efe9 100%)",
  }}
>
  {/* conteúdo com scroll */}
<div
  style={{
    flex: 1,
    overflowY: "auto",
    padding: "24px",
    minHeight: 0, // 👈 importante!
    maxHeight: "100vh",
  }}
>

   {/* Cabeçalho */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <Title level={2} style={{ margin: 0, color: "#2c3e50", fontWeight: 700 }}>
              Gestão de Chamadas por Agentes
            </Title>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Analise o desempenho dos agentes e visualize as chamadas
            </Text>
          </div>
          
          <Space size="middle" wrap>
            <div>
              <Text strong style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
                Período
              </Text>
              <RangePicker
                format="DD/MM/YYYY"
                value={datas}
                onChange={(values) => setDatas(values)}
                style={{ borderRadius: "8px", minWidth: '240px' }}
                size="middle"
              />
            </div>
            
            <div>
              <Text strong style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
                Selecionar Agente
              </Text>
              <Select
                placeholder="Selecione um agente"
                allowClear
                value={agenteSelecionado}
                onChange={(value) => setAgenteSelecionado(value)}
                style={{ width: 200 }}
                size="middle"
                loading={loadingDados}
                disabled={!datas || loadingDados}
              >
                {dadosAgentes.map((agente, index) => (
                  <Option key={index} value={agente.agente}>
                    {agente.agente}
                  </Option>
                ))}
              </Select>
            </div>
          </Space>
        </div>
      </div>

      {/* Loading quando está buscando dados */}
      {loadingDados && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: '16px' }}>Carregando dados...</Text>
        </div>
      )}

      {/* Cards de resumo do agente selecionado */}
      {agenteDetalhes && !loadingDados && (
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <Title level={3} style={{ color: "white", marginBottom: "16px" }}>
                {agenteDetalhes.agente}
              </Title>
   <Row 
  gutter={[16, 16]} 
  style={{ 
    display: "flex", 
    flexWrap: "wrap", 
    alignItems: "center",
    justifyContent: "flex-start",
  }}
>
  {stats.map((item, index) => (
    <Col 
      key={index}
      xs={24} 
      sm={12} 
      md={8}
      lg={6}
      style={{ 
        display: "flex", 
        alignItems: "center", 
        gap: "12px",
        whiteSpace: "nowrap",       // 👈 impede quebra de linha
      }}
    >
      <div
        style={{
          backgroundColor: "rgba(255,255,255,0.2)",
          color: "white",
          borderRadius: "12px",
          width: 48,
          height: 48,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "20px",
          flexShrink: 0,
        }}
      >
        {item.icon}
      </div>

      <div style={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "center" }}>
        <span style={{ color: "white", fontSize: "14px" }}>{item.title}:</span>
        <strong style={{ color: "white", fontSize: "16px" }}>{item.value}</strong>
      </div>
    </Col>
  ))}
</Row>


            </Card>
          </Col>
        </Row>
      )}

      {/* Gráfico de chamadas por dia do agente selecionado */}
      {agenteSelecionado && dadosGrafico.length > 0 && (
        <Card
          style={{
            marginBottom: "24px",
            borderRadius: "12px",
            boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
          }}
          title={
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
              Chamadas atendidas por dia - {agenteSelecionado}
            </span>
          }
          bordered={false}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={dadosGrafico}
              margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
            >
              <XAxis 
                dataKey="data" 
                tick={{ fontSize: 12 }} 
              />
              <YAxis />
              <RechartsTooltip 
                contentStyle={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                }}
                formatter={(value) => [value, "Chamadas atendidas"]}
                labelFormatter={(label) => `Data: ${label}`}
              />
              <Legend />
              <Bar 
                dataKey="chamadas" 
                fill="#1890ff" 
                name="Chamadas atendidas" 
                barSize={40}
                radius={[8, 8, 0, 0]} 
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Mensagem quando não há agente selecionado */}
      {!agenteSelecionado && datas && !loadingDados && (
        <Card
          style={{
            marginBottom: "24px",
            borderRadius: "12px",
            boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
            textAlign: 'center',
            padding: '40px'
          }}
          bordered={false}
        >
          <InfoCircleOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
          <Title level={4} style={{ color: '#666' }}>
            Selecione um agente para visualizar os dados
          </Title>
          <Text type="secondary">
            Escolha um operador na lista acima para ver as estatísticas detalhadas, gráficos e chamadas
          </Text>
        </Card>
      )}

      {/* Mensagem quando não há período selecionado */}
      {!datas && (
        <Card
          style={{
            marginBottom: "24px",
            borderRadius: "12px",
            boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
            textAlign: 'center',
            padding: '40px'
          }}
          bordered={false}
        >
          <InfoCircleOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
          <Title level={4} style={{ color: '#666' }}>
            Selecione um período para começar
          </Title>
          <Text type="secondary">
            Escolha um intervalo de datas no seletor acima para carregar os dados dos agentes
          </Text>
        </Card>
      )}

      {/* Tabela de chamadas - só mostra quando há agente selecionado */}
      {agenteSelecionado && (
        <Card
          bordered={false}
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ padding: '20px 20px 0 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <Title level={4} style={{ margin: 0 }}>
                Chamadas de {agenteSelecionado}
              </Title>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  Total: {filteredChamadas.length}
                </Tag>
              </div>
            </div>
          </div>
          
          <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 20px 20px" }}>
      <Table
  rowKey="id"
  columns={columns}
  dataSource={filteredChamadas}
  loading={loading}
  pagination={{
    pageSize: 1,
    showSizeChanger: true,
    showQuickJumper: true,
    position: ['bottomCenter'],
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} de ${total} chamadas`,
  }}
  scroll={{ x: 800, y: 'calc(100vh - 500px)' }} // 👈 altura dinâmica
/>

          </div>
        </Card>
      )}
  </div>
</div>

  );
};

export default GestaoChamadasAgentes;